import { cn } from '@/utils/cn';
import { ErrorMessage, Field } from 'formik';
import { forwardRef } from 'react';
import { InfoBubble } from './InfoBubble';

interface TextInputProps {
  label?: string;
  name?: string;
  type: string;
  error?: string | boolean;
  placeholder?: string;
  containerStyle?: string;
  labelStyle?: string;
  inputStyle?: string;
  isRequired?: boolean;
  disabled?: boolean;
  helper?: string;
  onChange?: (value: string) => void;
  useFormik?: boolean;
  value?: string;
  min?: number | string;
  max?: number | string;
  icon?: React.ReactNode;
  onIconClick?: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
  id?: string;
  autoComplete?: string;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      type,
      name,
      error,
      containerStyle,
      placeholder,
      labelStyle,
      inputStyle,
      isRequired = false,
      disabled = false,
      helper,
      onChange,
      useFormik = true,
      value,
      min,
      max,
      icon,
      onIconClick,
      inputRef,
      id,
      autoComplete,
    },
    ref,
  ) => {
    return (
      <div className={cn(`relative flex flex-col space-y-2`, containerStyle)}>
        {label && (
          <label
            className={cn(`text-xs font-medium text-gray-600`, labelStyle)}
          >
            {label}
            {isRequired && <span className="ml-1 text-danger">*</span>}
          </label>
        )}

        {isRequired && !label && (
          <span className="absolute left-0 top-1 text-xs text-danger">*</span>
        )}

        <div className="relative">
          {useFormik ? (
            <Field
              id={id}
              type={type}
              innerRef={ref || inputRef}
              disabled={disabled}
              name={name}
              placeholder={placeholder}
              min={min}
              max={max}
              autoComplete={autoComplete}
              className={cn(
                `w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-800 shadow-sm transition-colors focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200`,
                icon ? 'pr-10' : '',
                error
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : '',
                inputStyle,
              )}
            />
          ) : (
            <input
              id={id}
              type={type}
              ref={ref || inputRef}
              disabled={disabled}
              name={name}
              placeholder={placeholder}
              onChange={e => onChange?.(e.target.value)}
              value={value}
              min={min}
              max={max}
              autoComplete={autoComplete}
              className={cn(
                `w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-800 shadow-sm transition-colors focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200`,
                icon ? 'pr-10' : '',
                error
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : '',
                inputStyle,
              )}
            />
          )}
          {icon && (
            <span className="absolute inset-y-0 right-2 flex items-center">
              {onIconClick ? (
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={onIconClick}
                  className="p-1 text-gray-500 hover:text-sky-600 focus:outline-none"
                  disabled={disabled}
                >
                  {icon}
                </button>
              ) : (
                <span className="text-gray-400">{icon}</span>
              )}
            </span>
          )}
        </div>
        {name &&
          (useFormik ? (
            <ErrorMessage
              name={name}
              component="span"
              className="text-sm text-danger"
            />
          ) : (
            error && (
              <span className="mt-1 text-xs text-danger md:text-sm">
                {error}
              </span>
            )
          ))}
        {helper && <InfoBubble message={helper} className="mt-1" />}
      </div>
    );
  },
);
